<template>
  <v-dialog v-model="dialog" max-width="760" persistent scrollable v-drag-dialog content-class="draggable-dialog" aria-labelledby="change-plan-title">
    <!-- Activator -->
    <template v-slot:activator="{ on, attrs }">
      <v-btn color="primary" dark v-bind="attrs" v-on="on">Change Plan</v-btn>
    </template>

    <v-card outlined class="rounded-xl plan-dialog d-flex flex-column">
      <CardToolbarDialog title="Change Plan" title-id="change-plan-title" icon="mdi-credit-card-edit-outline" :disabled="isLoading" @cancel="cancel" />
      <v-divider />
      <div class="plan-dialog__body">
      <v-alert v-if="billingError" type="error" outlined dense role="alert">{{ billingError }} <v-btn text small color="error" @click="loadBilling">Retry</v-btn></v-alert>
      <div class="text-center mt-3 mb-6 subtitle-2 text--secondary">
        All plan changes are prorated and will apply immediately.
      </div>

      <!-- ✅ Plan Grid -->
      <v-container fluid>
        <v-alert v-if="plansError" type="error" outlined dense role="alert">{{ plansError }} <v-btn text small color="error" @click="fetchPlans">Retry</v-btn></v-alert>
        <v-skeleton-loader v-if="plansLoading" type="card@2" />
        <v-alert v-else-if="!plans.length && !plansError" type="info" outlined dense>
          No alternative plans are currently available for this brand.
        </v-alert>
        <v-row v-else>
          <v-col
            v-for="plan in plans"
            :key="plan.id"
            cols="12"
            sm="6"
            md="4"
            class="d-flex"
          >
            <v-card
              outlined
              class="plan-card flex-grow-1"
              :elevation="isCurrentPlan(plan) ? 8 : 2"
              :class="{ 'active-plan': isCurrentPlan(plan) }"
            >
              <!-- 🟢 Current Plan Chip -->
              <div
                v-if="isCurrentPlan(plan)"
                class="text-center mt-3"
              >
                <v-chip
                  color="success"
                  text-color="white"
                  small
                  class="font-weight-medium mb-2 current-chip"
                >
                  <v-icon left small>mdi-check-circle</v-icon>
                  Current Plan
                </v-chip>
              </div>

              <v-card-text class="text-center">
                <h3 class="font-weight-bold mb-1">{{ plan.name }}</h3>

                <div class="display-1 font-weight-bold mb-2">{{ planPrice(plan) }}</div>

                <div
                  v-if="!isCurrentPlan(plan) && plan.trialDays"
                  class="caption grey--text mb-4"
                >
                  First time subscribers receive
                  <strong>{{ plan.trialDays }}</strong> days trial. Cancel
                  anytime.
                </div>

                <!-- ✅ Choose Plan button with confirmation -->
                <v-btn
                  v-if="!isCurrentPlan(plan)"
                  color="primary"
                  rounded
                  depressed
                  :loading="isLoading && changingPlanId === planId(plan)"
                  :disabled="isLoading || billingLoading || !!billingError"
                  @click="confirmPlanChange(plan)"
                >
                  Choose Plan
                </v-btn>

                <v-chip
                  v-else
                  small
                  color="success"
                  outlined
                  class="font-weight-bold mt-3 elevation-1"
                >
                  Selected
                </v-chip>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>

      </div>
      <v-divider />

      <!-- Footer Actions -->
      <v-card-actions class="justify-space-between flex-wrap">
        <v-spacer />

        <v-dialog v-model="cancelSubscriptionDialog" max-width="420" aria-labelledby="cancel-account-title">
          <template v-slot:activator="{ on, attrs }">
            <v-btn text color="error" v-bind="attrs" v-on="on">
              Cancel Subscription
            </v-btn>
          </template>

          <v-card>
            <v-card-title id="cancel-account-title" class="headline">Cancel Account</v-card-title>
            <v-card-text>
              To cancel this entire subscription, please email us at
              <strong>{{ clientEmail }}</strong
              >. <br /><br />
              Contact support if you need help choosing another available plan.
            </v-card-text>
            <v-card-actions>
              <v-spacer />
              <v-btn text color="primary" @click="cancelSubscriptionDialog = false">
                Close
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import locationService from "../service";
import publicService from "../../../api/public";
import { mapGetters } from "vuex";

export default {
  name: "location-billing-update-plan",
  props: ["location"],
  data() {
    return {
      isLoading: false,
      billingLoading: false,
      changingPlanId: null,
      plansLoading: false,
      plansError: "",
      billingError: "",
      cancelSubscriptionDialog: false,
      dialog: false,
      plans: [],
      billing: null,
      clientEmail: process.env.VUE_APP_CLIENT_EMAIL,
    };
  },
  created() {
    this.fetchPlans();
    this.loadBilling();
  },
  computed: {
    ...mapGetters("brand", ["currentBrand"]),
  },
  methods: {
    cancel() {
      this.dialog = false;
    },

    async fetchPlans() {
      this.plansLoading = true;
      this.plansError = "";
      try {
        const response = await publicService.GetPlans({ audience: "LOCATION" }, this.currentBrand);
        this.plans = response.data;
      } catch (err) {
        this.plans = [];
        this.plansError = "Plans could not be loaded.";
      } finally {
        this.plansLoading = false;
      }
    },

    async loadBilling() {
      this.billingLoading = true;
      this.billingError = "";
      try {
        const response = await locationService.Billing.getInfo(
          this.location.id
        );
        this.billing = response.data;
      } catch (err) {
        this.billingError = "Current billing details could not be loaded.";
      } finally {
        this.billingLoading = false;
      }
    },

    planId(plan) {
      return plan.planId || plan.code;
    },

    isCurrentPlan(plan) {
      return !!this.billing && this.planId(plan) === this.billing.basePlanId;
    },

    planPrice(plan) {
      const amount = new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: String(plan.currency || "USD").toUpperCase(),
      }).format(Number(plan.amount || 0));
      const count = Number(plan.intervalCount) || 1;
      const interval = plan.interval || "billing period";
      return `${amount} every ${count > 1 ? `${count} ` : ""}${interval}${count > 1 ? "s" : ""}`;
    },

    async confirmPlanChange(plan) {
      if (this.isLoading || this.billingLoading || this.billingError || !this.billing) return;
      const result = await this.$swal.fire({
        title: `Change to ${plan.name}?`,
        text: `${this.currentPlanName()} → ${plan.name} (${this.planPrice(plan)}). This change is prorated and applies immediately.`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: this.$vuetify.theme.currentTheme.primary,
        confirmButtonText: "Yes, change it",
      });

      if (result.value) {
        this.changePlan(this.planId(plan));
      }
    },

    currentPlanName() {
      const current = this.plans.find(plan => this.isCurrentPlan(plan));
      return current ? current.name : "Current plan";
    },

    async changePlan(planId) {
      this.isLoading = true;
      this.changingPlanId = planId;
      this.$Progress.start();

      try {
        await locationService.Billing.changePlan(this.location.id, { planId });
        this.dialog = false;
        await this.loadBilling();
        this.$swal.fire({
          toast: true,
          position: "top-end",
          timer: 3000,
          icon: "success",
          title: "Plan was changed successfully.",
        });
        this.$emit("updated");
      } catch (error) {
        this.$swal.fire({
          toast: true,
          position: "top-end",
          timer: 3000,
          icon: "error",
          title: error.message || error.data.message,
        });
      } finally {
        this.isLoading = false;
        this.changingPlanId = null;
        this.$Progress.finish();
      }
    },
  },
};
</script>

<style scoped>
.plan-card {
  position: relative;
  transition: all 0.25s ease;
  border-radius: 14px;
  overflow: hidden;
}
.plan-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.06);
}
.active-plan {
  border-width: 2px;
  border-color: var(--v-success-base);
}
.current-chip {
  border-radius: 20px;
}
.plan-dialog { max-height: calc(100vh - 48px); }
.plan-dialog__body { overflow-y: auto; }
</style>
